import { defineComponent as _defineComponent } from 'vue'
import {
  computed,
  onMounted,
  ref,
  watch,
} from "vue";

import {
  useDataTableDefaults,
  useI18n,
  useSnackbar,
} from "@/composables";

import {
  User,
} from "@/scripts/Common";

import {
  FloatingActionButton,
} from "@/components/buttons";

import {
  hasAccessRights,
} from "@/authorization/AuthorizationService";

import {
  editIssuersPolicy,
} from "@/authorization/policies";

import {
  Issuer,
} from "@/model/Issuer";

import {
  loadIssuerFromStorageOrFirst,
} from "@/components/issuers/scripts/issuers/helper";

import {
  saveItemToStorage,
} from "@/scripts/Common/componentStorage";

import {
  DEFAULT_SORT_COLUMN_FETCH_ISSUERS,
  ISSUER_ID_STORAGE_KEY,
} from "@/components/issuers/scripts";

import {
  debounce,
  delayFunctionCall,
} from "@/scripts/Common/HelperFunctions";

import {
  fetchIssuers,
  FetchIssuersParam,
} from "@/components/issuers/scripts/issuers";

import {
  fetchIssuerIdentityUsers,
  FetchIssuerIdentityUsersParam,
  MIN_EMAIL_FILTER_LENGTH,
} from "@/components/issuers/scripts/issuerIdentityUsers";

import InfoSnackbar from "../children/InfoSnackbar.vue";

import {
  AddIssuerPortalUserDialog,
  CreatePasswordChangeTicketResultDialog
} from "@/components/issuers/children";

import {
  changeIdentityUserState,
  createPasswordChangeTicket,
  resetPasswordViaEmail
} from "@/components/children/identityUsers/scripts";

import {
  UpdateIdentityUserDialog,
} from "@/components/children";

import {
  deleteIdentityUser,
} from "@/components/children/identityUsers/scripts";

import ConfirmationDialog from "@/components/base/ConfirmationDialog.vue";

import {
  IssuerStatus,
} from "@/Enums";
import {TooltipListItem} from "@/components/base";


export default /*#__PURE__*/_defineComponent({
  __name: 'IssuerUserManagement',
  emits: [
  'onFetchError',
],
  setup(__props, { emit: emits }) {


const {
  t,
} = useI18n();

const {
  pagination,
  dataTableFooter,
  dataTableOptions,
} = useDataTableDefaults();

const snackbarRef = ref<InstanceType<typeof InfoSnackbar>>();

const {
  snackbarStatus,
  snackbarText,
  showSnackbar,
} = useSnackbar(snackbarRef);



const users = ref<User[]>([]);
const selectedUser = ref<User>();
const emailFilter = ref<string>('');
const comboSelection = ref<Issuer>();
const issuers = ref<Issuer[]>([]);
const ignoreIssuerSearchInputChange = ref(false);
const issuerSearchInput = ref<string>();
const addUserActive = ref(false);
const updateUserActive = ref(false);
const confirmationDialog = ref<InstanceType<typeof ConfirmationDialog>>();
const createPasswordChangeTicketResultDialogRef = ref<InstanceType<typeof CreatePasswordChangeTicketResultDialog>>();

const allowedIssuerStates = [
  IssuerStatus.Confirmed,
  IssuerStatus.Canceled,
];

const hasNoEditRights = computed(() => {
  const isInvalidIssuer = !comboSelection.value
    || !comboSelection.value.Status
    || !allowedIssuerStates.includes(comboSelection.value.Status);

  return !hasAccessRights(editIssuersPolicy) || !comboSelection.value || isInvalidIssuer;
});

const headers = computed(() => {
  return [
    {
      align: "start",
      text: t("table_header_email"),
      value: "email",
      key: "email"
    },
    {
      align: "start",
      text: t("table_header_first_name"),
      value: "firstName",
      key: "firstName",
      filterable: false,
      sortable: false
    },
    {
      align: "start",
      text: t("table_header_last_name"),
      value: "lastName",
      key: "lastName",
      filterable: false,
      sortable: false
    },
    {
      align: "start",
      text: t("table_header_block_status"),
      value: "isBlocked",
      key: "isBlocked",
      filterable: false,
      sortable: false
    },
    {
      align: "start",
      text: t("table_header_verify_status"),
      value: "isEmailVerified",
      key: "isEmailVerified",
      filterable: false,
      sortable: false,
    },
    {
      align: "start",
      text: t("table_header_actions"),
      value: "actions",
      filterable: false,
      sortable: false,
    }
  ]
});

const onHandleFetchError = ({error}: {
  error: any
}) => {
  emits('onFetchError', {error})
};

const loadUsers = async () => {
  if (!comboSelection.value?.Id) return;

  const fetchArgs: FetchIssuerIdentityUsersParam = {
    issuerId: comboSelection.value.Id,
    pageNumber: dataTableOptions.value.pagination.page,
    pageSize: dataTableOptions.value.pagination.itemsPerPage,
  };

  const sortColumn = dataTableOptions.value.sorting.sortBy;
  const sortDirection = dataTableOptions.value.sorting.sortDesc;

  // add sorting, if set
  if(sortColumn){
    fetchArgs.sortColumn = sortColumn
    fetchArgs.sortDirection = sortDirection ? "desc" : "asc";
  }

  if (emailFilter.value) fetchArgs.email = emailFilter.value;

  dataTableOptions.value.loading = true;

  const {
    response,
    error,
    totalItemCount
  } = await fetchIssuerIdentityUsers(fetchArgs);

  dataTableOptions.value.loading = false;

  if (error) {
    onHandleFetchError({error});
    return;
  }

  users.value = response ?? [];
  dataTableOptions.value.pagination.totalItemCount = totalItemCount ?? 0;
};

const onHandleEditUser = (user: User) => {
  selectedUser.value = user;
  updateUserActive.value = true;
};

const onHandleChangeUserState = async (userId: string, state: 'block' | 'unblock') => {
  const { error } = await changeIdentityUserState(userId, state);

  if (error) {
    onHandleFetchError({error});
    return;
  }

  showSnackbar({
    statusText: [t('user_updated_successfully')],
    status: "success"
  });

  dataTableOptions.value.loading = true;

  delayFunctionCall(() => loadUsers(), 2000);
};

const onHandleDeleteUser = (user: User) => {
  selectedUser.value = user;
  confirmationDialog.value?.Show();
};

const onResetPasswordViaEmail = async (user: User) => {
  selectedUser.value = user;

  dataTableOptions.value.loading = true;

  const { error } = await resetPasswordViaEmail(selectedUser.value.userId);

  dataTableOptions.value.loading = false;

  if (error) {
    onHandleFetchError({error});
    return;
  }

  showSnackbar({
    statusText: [t('reset_password_via_email_success')],
    status: "success"
  });
};

const onHandleCreatePasswordChangeTicket = async (user: User) => {
  selectedUser.value = user;

  dataTableOptions.value.loading = true;

  const { error, response } = await createPasswordChangeTicket(selectedUser.value.userId);

  dataTableOptions.value.loading = false;

  if (error) {
    onHandleFetchError({error});
    return;
  }

  createPasswordChangeTicketResultDialogRef.value!.Show(response.ticketUrl);
};


const onHandleConfirmDeleteUser = async () => {
  if(!selectedUser.value) return;

  dataTableOptions.value.loading = true;
  const { error } = await deleteIdentityUser(selectedUser.value.userId)

  if (error) {
    onHandleFetchError({error});
    dataTableOptions.value.loading = false;
    return;
  }

  delayFunctionCall(() => loadUsers(), 2000);
};

const onHandleAddUser = () => {
  addUserActive.value = true;
};

const onHandleUpdateIdentityUserDialogCancel = () => {
  updateUserActive.value = false;
  selectedUser.value = undefined;
};

const onHandleUpdateIdentityUserDialogConfirm = async () => {
  updateUserActive.value = false;
  selectedUser.value = undefined;
  dataTableOptions.value.loading = true;

  showSnackbar({
    statusText: [t('user_updated_successfully')],
    status: "success"
  });

  delayFunctionCall(() => loadUsers(), 2000);
};

const onHandleAddIssuerPortalUserDialogCancel = () => {
  addUserActive.value = false;
};

const onHandleAddIssuerPortalUserDialogConfirm = async () => {
  addUserActive.value = false;

  dataTableOptions.value.loading = true;

  showSnackbar({
    statusText: [t('user_added_successfully')],
    status: "success"
  });

  delayFunctionCall(() => loadUsers(), 2000);
};

const loadIssuers = async () => {
  const fetchArgs: FetchIssuersParam = {
    sortColumn: DEFAULT_SORT_COLUMN_FETCH_ISSUERS
  };

  if (issuerSearchInput.value) fetchArgs.companyName = issuerSearchInput.value;

  const {response, error} = await fetchIssuers(fetchArgs);
  if (error) {
    onHandleFetchError({error});
    return;
  }

  issuers.value = response ?? [];
};

const onHandleSelectionChange = () => {
  const issuerId = comboSelection.value?.Id; //  comboSelection.value could be something else than an Issuer (string companyname without results)

  if (!issuerId || !(comboSelection.value instanceof Issuer)) return;
  ignoreIssuerSearchInputChange.value = true; // avoid a reload of the issuer list when the issuer is selected from the combobox
  saveItemToStorage(issuerId, ISSUER_ID_STORAGE_KEY);
}

const initializeIssuer = async () => {
  const {response, error} = await loadIssuerFromStorageOrFirst();

  if (error) {
    onHandleFetchError({error});
    return;
  }

  comboSelection.value = response;
}

onMounted(async () => {
  await initializeIssuer();
});

watch(() => issuerSearchInput.value, debounce( () => {
  if (ignoreIssuerSearchInputChange.value) {
    ignoreIssuerSearchInputChange.value = false;
    return;
  }
  loadIssuers();
}));

watch(() => emailFilter.value, debounce(() => {
  if(!!emailFilter.value && emailFilter.value.length < MIN_EMAIL_FILTER_LENGTH) return;
  if(dataTableOptions.value.pagination.page === 1){
    loadUsers();
    return;
  }

  //Will trigger watcher below no separate loading necessary
  dataTableOptions.value.pagination.page = 1;
}));

watch(() => [
  dataTableOptions.value.pagination.page,
  dataTableOptions.value.pagination.itemsPerPage,
  dataTableOptions.value.sorting,
  comboSelection.value
], () => {
  loadUsers();
}, {deep: true});


return { __sfc: true,t, pagination, dataTableFooter, dataTableOptions, snackbarRef, snackbarStatus, snackbarText, showSnackbar, emits, users, selectedUser, emailFilter, comboSelection, issuers, ignoreIssuerSearchInputChange, issuerSearchInput, addUserActive, updateUserActive, confirmationDialog, createPasswordChangeTicketResultDialogRef, allowedIssuerStates, hasNoEditRights, headers, onHandleFetchError, loadUsers, onHandleEditUser, onHandleChangeUserState, onHandleDeleteUser, onResetPasswordViaEmail, onHandleCreatePasswordChangeTicket, onHandleConfirmDeleteUser, onHandleAddUser, onHandleUpdateIdentityUserDialogCancel, onHandleUpdateIdentityUserDialogConfirm, onHandleAddIssuerPortalUserDialogCancel, onHandleAddIssuerPortalUserDialogConfirm, loadIssuers, onHandleSelectionChange, initializeIssuer, FloatingActionButton, MIN_EMAIL_FILTER_LENGTH, InfoSnackbar, AddIssuerPortalUserDialog, CreatePasswordChangeTicketResultDialog, UpdateIdentityUserDialog, ConfirmationDialog, TooltipListItem }
}

})